import React from 'react';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _filter from 'lodash/filter';
import _map from 'lodash/map';
import _values from 'lodash/values';
import _toString from 'lodash/toString';
import _some from 'lodash/some';
import PropTypes from 'prop-types';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mDevice from 'models/device';
import mDeviceSlave from 'models/deviceSlave';
// Custom
import { deviceSlaveDataShape } from 'constants/PropTypesShapes';
import { configurationDeviceSlavesShowPath, configurationDeviceSlavesReplacePath } from 'installations/navigation';
import CollapseBoxTemplate from 'components/templates/CollapseBoxTemplate';
import { ConfigureTextButton, ReplaceTextButton, DeleteTextButton } from 'components/buttons/TextButtons';
import ResourceAsyncStatusItem from 'components/items/ResourceAsyncStatusItem';
import withEntitiesInformation from 'installations/assets/hocs/WithEntitiesInformation';
import Callout from 'components/misc/Callout';
import TriggerItem from './TriggerItem';
import RelayItem from './RelayItem';
import InputItem from './InputItem';

const SlaveItem = ({
  id, deviceId, deviceSlave, onDelete,
}) => {
  const protocolTriggersArray = mDevice.sortTriggers(_filter(_values(deviceSlave.triggers), (t) => (t.protocols && t.protocols.length > 0)));
  const hasEquipment = _some(protocolTriggersArray, (trigger) => (!!trigger.equipment));
  const relaysArray = mDevice.sortRelays(deviceSlave.relays);
  const inputsArray = mDevice.sortInputs(deviceSlave.inputs);

  // Permissions
  const { pInstallation: { configWrite } } = permissions;

  // Check if slave is motherboard
  const isMotherboard = mDeviceSlave.isMotherboard(deviceSlave);

  // Buttons
  const buttons = [
    <OnlyWith
      key="bConfig"
      permissions={{
        needed: configWrite,
        permissionsOrContext: 'installationPermissions',
      }}
    >
      <ConfigureTextButton primary path={configurationDeviceSlavesShowPath(id, deviceId, deviceSlave.id)} />
    </OnlyWith>,
  ];
  if (!isMotherboard) {
    buttons.push(
      <OnlyWith
        key="bReplace"
        permissions={{
          needed: configWrite,
          permissionsOrContext: 'installationPermissions',
        }}
      >
        <ReplaceTextButton primary path={configurationDeviceSlavesReplacePath(id, deviceId, deviceSlave.id)} />
      </OnlyWith>,
      <OnlyWith
        key="bDelete"
        permissions={{
          needed: configWrite,
          permissionsOrContext: 'installationPermissions',
        }}
      >
        <DeleteTextButton primary onClick={() => onDelete(deviceSlave)} />
      </OnlyWith>,
    );
  }

  return (
    <CollapseBoxTemplate
      title={<LocalizedMessage id="title.deviceSlave" values={{ isMotherboard, desc: mDeviceSlave.installationDesc(deviceSlave) }} />}
      titleSize="sm"
      buttons={buttons}
      initiallyClosed
    >
      <div className="row m-0 px-3 pb-3">
        <div className="col-lg-4 col-md-6 col-sm-12 pl-0">
          <ResourceAsyncStatusItem resource={deviceSlave} idKey="idSlave" />
        </div>
        {!!protocolTriggersArray && (
          <div className="w-100 pt-4">
            <Callout>
              <LocalizedMessage id="subtitle.equipments" />
            </Callout>
            <div className="row information-item-wrapper pt-3">
              {!hasEquipment && (
                <div className="col-12 pt-3">
                  <em><LocalizedMessage id="info.noConnectedEquipment" /></em>
                </div>
              )}
              {_map(protocolTriggersArray, (trigger) => (
                trigger.equipment && (
                  <div key={_toString(trigger.id)} className="col-lg-4 col-md-6 col-sm-12">
                    <TriggerItem trigger={trigger} />
                  </div>
                )
              ))}
            </div>
          </div>
        )}
        {!!relaysArray && (
          <div className="w-100 pt-4">
            <Callout>
              <LocalizedMessage id="subtitle.relays" />
            </Callout>
            <div className="row information-item-wrapper pt-3">
              {_map(relaysArray, (relay) => (
                <div key={_toString(relay.id)} className="col-lg-4 col-md-6 col-sm-12">
                  <RelayItem relay={relay} />
                </div>
              ))}
            </div>
          </div>
        )}
        {!!inputsArray && (
          <div className="w-100 pt-4">
            <Callout>
              <LocalizedMessage id="subtitle.inputs" />
            </Callout>
            <div className="row information-item-wrapper pt-3">
              {_map(inputsArray, (input) => (
                <div key={_toString(input.id)} className="col-lg-4 col-md-6 col-sm-12">
                  <InputItem input={input} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CollapseBoxTemplate>
  );
};

SlaveItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  deviceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  deviceSlave: deviceSlaveDataShape.isRequired,
  onDelete: PropTypes.func.isRequired,
};

module.exports = withEntitiesInformation(SlaveItem);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/configuration/SlaveItem.js